import { ITelemetryProperties, ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { ErrorObject, ActivityData, ActivityResultType } from '@fluidx/office-fluid-types';
import { sendActivityEvent } from './LoggerHelpers';
import { v4 as uuidv4 } from 'uuid';

let inflightActivities = new Set<ActivityTracker>();

export interface ActivityTrackerOptions {
  /**
   * A set of properties to include in all log statements made by this instance of the activity tracker.
   */
  additionalPropsToLog?: ITelemetryProperties;

  /**
   * Flag indicating whether the activity should create marks and measures useful for browser profiling.
   * This does not impact what we log, it only defines whether we use the User Timing DOM API or not
   * https://developer.mozilla.org/en-US/docs/Web/API/User_Timing_API
   */
  addUserTiming?: boolean;
}

/**
 * Log all inflight activities as aborted when this method is called.
 *
 * WARNING: calling this method will abort ALL activities in the current window context.
 * Thus, this method is only safe to call on the **inside** of an iframe window,
 * NOT outside an iframe or in a div scenario. (Otherwise, if two components are starting up
 * at the same time, calling this method from one would abort all unrelated activities in the other).
 */
export function abortInflightActivities() {
  inflightActivities.forEach((activityTracker) => activityTracker.cancel());
}

/**
 * Helper class to log activities with success and duration. Only logs when setResult is called.
 */
export class ActivityTracker {
  /** The start point for this activity, relative to performance.now. This is not adjusted to an origin time. */
  public readonly startTime: number;

  /** Sub-activities used to log measures of an action inside an activity  */
  public readonly subActivities: {
    [subActivityName: string]: {
      startTime: number;
      startTimeSinceEpoch: number;
    };
  };

  /** The start time (as epoch time) that gets added to the logs and used in perf dashboards */
  public readonly startTimeSinceEpoch: number;

  /** Additional properties to include in all log statements */
  private additionalPropsToLog?: ITelemetryProperties;

  /** Flag indicating whether the activity should create marks and measures for browser profiling. This does not impact what we log */
  private addUserTiming?: boolean;

  private readonly activityStartName: string;
  private readonly activityEndName: string;

  /** A unique identifier for appending to the name used in performance.measure */
  private readonly identifier;

  /**
   * A flag indicating whether the activity has already sent its result
   * (to ensure we don't send two results or add two ends marks for the same activity).
   * Once `.setResult` or `.cancel` have been called, the result of the activity cannot be changed.
   */
  private hasAlreadyResolved?: boolean;

  protected constructor(
    private readonly activityName: string,
    private logger: ITelemetryBaseLogger | undefined,
    activityTrackerOptions?: ActivityTrackerOptions
  ) {
    this.additionalPropsToLog = activityTrackerOptions?.additionalPropsToLog;
    this.addUserTiming = activityTrackerOptions?.addUserTiming;

    this.subActivities = {};
    this.identifier = uuidv4();

    this.activityStartName = `${this.activityName}-start-${this.identifier}`;
    this.activityEndName = `${this.activityName}-end-${this.identifier}`;

    // Perf Profiles: Mark the start of event for browser profiling
    this.addUserTiming && performance.mark(this.activityStartName);

    this.startTime = performance.now();
    this.startTimeSinceEpoch = Date.now();
  }

  /**
   * Creates an ActivityTracker instance
   * @param activityName The name of the activity
   * @param activityTrackerOptions optional configuration arguments to the activity tracker
   */
  public static start(
    activityName: string,
    logger: ITelemetryBaseLogger | undefined,
    activityTrackerOptions?: ActivityTrackerOptions
  ): ActivityTracker {
    const activityTracker = new ActivityTracker(activityName, logger, activityTrackerOptions);
    inflightActivities.add(activityTracker);
    return activityTracker;
  }

  /**
   * Start to track a sub activity.
   * @param subActivityName name of the sub activity.
   */
  public markSubActivityStart(subActivityName: string): void {
    this.subActivities[subActivityName] = {
      startTime: performance.now(),
      startTimeSinceEpoch: Date.now()
    };

    this.addUserTiming && performance.mark(this.getSubActivityName(subActivityName, 'start'));
  }

  /**
   * Complete a sub activity measurement.
   * @param subActivityName name of the sub activity.
   * @param subActivityAdditionalProps additional properties.
   */
  public markSubActivityEnd(subActivityName: string, subActivityAdditionalProps?: ITelemetryProperties): void {
    if (!this.subActivities[subActivityName]) {
      // no such sub activity.
      return;
    }

    if (this.addUserTiming) {
      const endName = this.getSubActivityName(subActivityName, 'end');
      performance.mark(endName);
      performance.measure(subActivityName, this.getSubActivityName(subActivityName, 'start'), endName);
    }

    this.additionalPropsToLog = {
      [`startTime_${subActivityName}`]: this.subActivities[subActivityName].startTimeSinceEpoch,
      [`durationMs_${subActivityName}`]: Math.round(performance.now() - this.subActivities[subActivityName].startTime),
      ...this.additionalPropsToLog,
      ...subActivityAdditionalProps
    };
  }

  /**
   * Ends the activity and logs it using the provided logger and performance markers.
   * @param succeeded Wether the activity was successful or not.
   * @param additionalProps Properties to be logged as data. These additional props will overwrite any additional props with the same name that were specified when the activity tracker was created.
   * @param error Error related to the activity.
   * @param options Options that configure the behavior of set result
   */
  public setResult(
    succeeded: boolean,
    additionalProps?: ITelemetryProperties,
    error?: ErrorObject,
    options?: {
      /** There are some scenarios where we want to skip logging for set result */
      skipLogging: boolean;
    }
  ) {
    this.logActivityResult(succeeded, additionalProps, error, options?.skipLogging);
  }

  /**
   * Ends the activity and logs the Activity.Result.Type as Cancelled
   * @param succeeded Whether the activity qualifies as successful
   * @param additionalProps Properties to be logged as data. These additional props will overwrite any additional props with the same name that were specified when the activity tracker was created.
   * @param error Error related to the activity.
   */
  public cancel(succeeded?: boolean, additionalProps?: ITelemetryProperties, error?: ErrorObject) {
    this.logActivityResult(succeeded, additionalProps, error, false /*skipLogging*/, true /*cancelled*/);
  }

  /**
   * Add properties to be included when setResult() is called.
   * If the activity is ready to be logged, call setResult() with additionalProps instead.
   */
  public addAdditionalProps(additionalProps: ITelemetryProperties) {
    this.additionalPropsToLog = {
      ...this.additionalPropsToLog,
      ...additionalProps
    };
  }

  /**
   * Cancels the activity if it's still pending, and clears any performance measures/marks
   * to avoid leaking memory.
   */
  public dispose() {
    // If the activity hasn't already resolved/cancelled, this will cancel it (otherwise, it's a no-op).
    // Setting an activity result also releases the logger object, if it wasn't already been released.
    this.cancel();

    const clearMarks = (name: string) => performance.clearMarks(name);
    const clearMeasures = (name: string) => performance.clearMeasures(name);

    // Clear all browser marks and measure if userTiming API was used in this activity
    if (this.addUserTiming) {
      clearMarks(this.activityStartName);
      clearMarks(this.activityEndName);
      clearMeasures(this.getMeasureName());

      for (const subActivityName in this.subActivities) {
        clearMarks(this.getSubActivityName(subActivityName, 'start'));
        clearMarks(this.getSubActivityName(subActivityName, 'end'));
        clearMeasures(subActivityName);
      }
    }
  }

  private logActivityResult(
    succeeded?: boolean,
    additionalProps?: ITelemetryProperties,
    error?: ErrorObject,
    skipLogging?: boolean,
    cancelled?: boolean
  ) {
    if (this.hasAlreadyResolved) {
      return;
    }

    this.hasAlreadyResolved = true;

    // Perf Profiles: Mark the end of event for browser profiling
    this.addUserTiming && performance.mark(this.activityEndName);
    const currentTickMs = performance.now();
    const durationMs = Math.round(currentTickMs - this.startTime);

    if (this.logger && !skipLogging) {
      const activityEvent: ActivityData = {
        eventName: this.activityName,
        startTime: this.startTimeSinceEpoch,
        durationMs,
        succeeded,
        resultType: cancelled
          ? ActivityResultType.Cancelled
          : succeeded
          ? ActivityResultType.Success
          : ActivityResultType.Failure,
        ...this.additionalPropsToLog,
        ...additionalProps
      };

      sendActivityEvent(this.logger, activityEvent, error);
    }

    this.logger = undefined;
    inflightActivities.delete(this);

    // Perf Profiles: Measure perf buffer between start and end of event for browser profiling
    this.addUserTiming && performance.measure(this.getMeasureName(), this.activityStartName, this.activityEndName);
  }

  private getMeasureName(): string {
    return `${this.activityName}-${this.identifier}`;
  }

  private getSubActivityName(name: string, suffix: 'start' | 'end') {
    return `${name}-${suffix}-${this.identifier}`;
  }
}
